import { useState, useMemo, useCallback } from "react";
import { cn } from "../../lib/utils";
import { IconFolder } from "nucleo-glass";
import { NucleoIcon } from "./NucleoIcon";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowRight01Icon, Home01Icon, FolderTransferIcon } from "@hugeicons/core-free-icons";
import * as Dialog from "@radix-ui/react-dialog";
import type { AssetFolder } from "../../hooks/useAssets";

// ── Types ───────────────────────────────────────────────────────

interface FolderTreeNode {
  id: string;
  name: string;
  parentId: string | null;
  children: FolderTreeNode[];
}

interface MoveAssetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetTitle: string;
  currentFolderId: string | null;
  folders: AssetFolder[];
  onMove: (folderId: string | undefined) => void;
}

// ── Build tree from flat folders ────────────────────────────────

function buildTree(folders: AssetFolder[]): FolderTreeNode[] {
  const map = new Map<string, FolderTreeNode>();

  for (const f of folders) {
    map.set(f.id, { id: f.id, name: f.name, parentId: f.parentId, children: [] });
  }

  const roots: FolderTreeNode[] = [];
  for (const node of map.values()) {
    if (node.parentId && map.has(node.parentId)) {
      map.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  // Sort children alphabetically at every level
  function sortChildren(nodes: FolderTreeNode[]) {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    for (const n of nodes) sortChildren(n.children);
  }
  sortChildren(roots);

  return roots;
}

// ── Tree row ────────────────────────────────────────────────────

function FolderTreeRow({
  node,
  depth,
  selectedId,
  currentFolderId,
  onSelect,
  expandedIds,
  onToggleExpand,
}: {
  node: FolderTreeNode;
  depth: number;
  selectedId: string | null;
  currentFolderId: string | null;
  onSelect: (id: string) => void;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
}) {
  const isSelected = selectedId === node.id;
  const isCurrent = currentFolderId === node.id;
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);

  return (
    <>
      <button
        type="button"
        onClick={() => onSelect(node.id)}
        className={cn(
          "w-full flex items-center gap-[var(--spacing-2)] py-[var(--spacing-1_5)]",
          "rounded-[var(--radius-sm)] text-left transition-all duration-[var(--duration-fast)] cursor-pointer",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
          isSelected
            ? "bg-[var(--color-accent-subtle-bg)] text-[var(--color-accent-600)] border border-[var(--color-accent-400)]/30"
            : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] border border-transparent",
        )}
        style={{ paddingLeft: `calc(var(--spacing-2) + ${depth} * var(--spacing-4))` }}
      >
        {/* Expand/collapse toggle */}
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(node.id);
            }}
            className={cn(
              "w-[16px] h-[16px] flex items-center justify-center shrink-0",
              "rounded-[var(--radius-sm)] hover:bg-[var(--color-bg-hover)]",
              "transition-transform duration-[var(--duration-fast)] cursor-pointer",
              isExpanded && "rotate-90",
            )}
          >
            <HugeiconsIcon icon={ArrowRight01Icon} size={10} />
          </button>
        ) : (
          <span className="w-[16px] shrink-0" />
        )}

        {/* Folder icon */}
        <span className={cn(
          "shrink-0 transition-colors duration-[var(--duration-fast)]",
          isSelected ? "text-[var(--color-accent-500)]" : "text-[var(--color-accent-400)]/60",
        )}>
          <NucleoIcon icon={<IconFolder size={16} />} />
        </span>

        {/* Name */}
        <span className="text-[13px] truncate flex-1">{node.name}</span>

        {/* Current location indicator */}
        {isCurrent && (
          <span className="text-[10px] font-medium text-[var(--color-text-muted)] bg-[var(--color-bg-hover)] px-[var(--spacing-1_5)] py-[1px] rounded-[var(--radius-sm)] shrink-0">
            current
          </span>
        )}
      </button>

      {/* Render children */}
      {hasChildren && isExpanded && (
        <div className="animate-dropdown-in">
          {node.children.map((child) => (
            <FolderTreeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              currentFolderId={currentFolderId}
              onSelect={onSelect}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </>
  );
}

// ── Component ───────────────────────────────────────────────────

export function MoveAssetDialog({
  open,
  onOpenChange,
  assetTitle,
  currentFolderId,
  folders,
  onMove,
}: MoveAssetDialogProps) {
  // null = root selected, string = folder id selected
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const tree = useMemo(() => buildTree(folders), [folders]);

  // Auto-expand folders on the path to current folder
  useMemo(() => {
    if (!open) return;
    const toExpand = new Set<string>();
    // Expand all ancestors of every folder (so tree is useful)
    for (const f of folders) {
      let parentId = f.parentId;
      while (parentId) {
        toExpand.add(parentId);
        const parent = folders.find((p) => p.id === parentId);
        parentId = parent?.parentId ?? null;
      }
    }
    setExpandedIds(toExpand);
    setSelectedId(null);
  }, [open, folders]);

  const handleToggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const isRootSelected = selectedId === null;
  const isSameLocation =
    (selectedId === null && currentFolderId === null) ||
    (selectedId === currentFolderId);

  function handleConfirm() {
    if (isSameLocation) return;
    onMove(selectedId ?? undefined);
    onOpenChange(false);
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px]",
            "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
          )}
        />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
            "w-full max-w-[380px] rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-surface)] shadow-lg overflow-hidden",
            "data-[state=open]:animate-page-enter",
            "focus:outline-none",
          )}
        >
          {/* Header */}
          <div className="px-[var(--spacing-4)] pt-[var(--spacing-4)] pb-[var(--spacing-2)]">
            <div className="flex items-center gap-[var(--spacing-2)] text-[var(--color-accent-500)]">
              <HugeiconsIcon icon={FolderTransferIcon} size={16} />
              <Dialog.Title className="text-[15px] font-semibold text-[var(--color-text-primary)]">
                Move asset
              </Dialog.Title>
            </div>
            <Dialog.Description className="text-[12px] text-[var(--color-text-muted)] mt-[var(--spacing-1)]">
              Choose a destination for{" "}
              <span className="font-medium text-[var(--color-text-primary)]">{assetTitle}</span>
            </Dialog.Description>
          </div>

          {/* Tree */}
          <div className="px-[var(--spacing-2)] py-[var(--spacing-2)] max-h-[320px] overflow-y-auto">
            {/* Root / Assets (top level) */}
            <button
              type="button"
              onClick={() => setSelectedId(null)}
              className={cn(
                "w-full flex items-center gap-[var(--spacing-2)] py-[var(--spacing-1_5)] px-[var(--spacing-2)]",
                "rounded-[var(--radius-sm)] text-left transition-all duration-[var(--duration-fast)] cursor-pointer",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
                isRootSelected
                  ? "bg-[var(--color-accent-subtle-bg)] text-[var(--color-accent-600)] border border-[var(--color-accent-400)]/30"
                  : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] border border-transparent",
              )}
            >
              <span className="w-[16px] shrink-0" />
              <span className={cn(
                "shrink-0 transition-colors duration-[var(--duration-fast)]",
                isRootSelected ? "text-[var(--color-accent-500)]" : "text-[var(--color-text-muted)]",
              )}>
                <HugeiconsIcon icon={Home01Icon} size={16} />
              </span>
              <span className="text-[13px] font-medium">Assets (root)</span>
              {currentFolderId === null && (
                <span className="text-[10px] font-medium text-[var(--color-text-muted)] bg-[var(--color-bg-hover)] px-[var(--spacing-1_5)] py-[1px] rounded-[var(--radius-sm)] shrink-0 ml-auto">
                  current
                </span>
              )}
            </button>

            {/* Folder tree */}
            {tree.length > 0 ? (
              <div className="mt-[var(--spacing-0_5)]">
                {tree.map((node) => (
                  <FolderTreeRow
                    key={node.id}
                    node={node}
                    depth={0}
                    selectedId={selectedId}
                    currentFolderId={currentFolderId}
                    onSelect={setSelectedId}
                    expandedIds={expandedIds}
                    onToggleExpand={handleToggleExpand}
                  />
                ))}
              </div>
            ) : (
              <p className="text-[12px] text-[var(--color-text-muted)] px-[var(--spacing-2)] py-[var(--spacing-3)] text-center">
                No folders yet. Create a folder first.
              </p>
            )}
          </div>

          {/* Footer */}
          <div className={cn(
            "flex items-center justify-between gap-[var(--spacing-2)]",
            "px-[var(--spacing-4)] py-[var(--spacing-3)]",
            "border-t border-[var(--color-border-stroke)] bg-[var(--color-bg-canvas)]/50",
          )}>
            {/* Destination preview */}
            <div className="flex items-center gap-[var(--spacing-1_5)] min-w-0 flex-1">
              <span className="text-[11px] text-[var(--color-text-muted)] shrink-0">→</span>
              <span className="text-[12px] text-[var(--color-text-muted)] truncate">
                {selectedId === null
                  ? "Assets (root)"
                  : folders.find((f) => f.id === selectedId)?.name ?? "Unknown"}
              </span>
            </div>

            <div className="flex items-center gap-[var(--spacing-2)] shrink-0">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className={cn(
                    "h-[32px] px-[var(--spacing-3)] text-[13px] font-medium",
                    "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]",
                    "rounded-[var(--radius-md)] transition-colors cursor-pointer",
                  )}
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isSameLocation}
                className={cn(
                  "h-[32px] px-[var(--spacing-4)] text-[13px] font-medium text-white",
                  "rounded-[var(--radius-md)]",
                  "bg-gradient-to-b from-[var(--color-accent-400)] to-[var(--color-accent-600)]",
                  "hover:from-[var(--color-accent-300)] hover:to-[var(--color-accent-500)]",
                  "disabled:opacity-40 disabled:cursor-not-allowed",
                  "transition-all cursor-pointer",
                )}
              >
                Move here
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
